import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';

import { formatCompact, formatSnapRate, SNAP_SUPPRESSED_NOTE } from '../../data/impactTypes';

import type { AreaCard } from './OverviewPanel';
import { overviewPanelStyles as styles } from './OverviewPanel.styles';
import { OVERVIEW_METRIC_TOOLTIPS } from './overviewTooltips';
import { AreaStat, InfoTooltip } from './shared';

interface SelectedAreasListProps {
  areaCards: AreaCard[];
  maxAreas: number;
  /** Remove by the area's FIPS. */
  onRemoveArea: (fips: string) => void;
  onClearAll: () => void;
}

/**
 * "X of N selected" + Clear all + the per-area cards — shared by the
 * desktop `OverviewPanel` and the mobile bottom sheet's "Selection" tab.
 */
export function SelectedAreasList({
  areaCards,
  maxAreas,
  onRemoveArea,
  onClearAll,
}: SelectedAreasListProps) {
  const hasSelection = areaCards.length > 0;

  return (
    <>
      <Box sx={styles.selectedHead}>
        <Typography component="span" sx={styles.selectedCount}>
          {areaCards.length} OF {maxAreas} SELECTED
        </Typography>
        {hasSelection && (
          <Link component="button" type="button" onClick={onClearAll} sx={styles.clearAll}>
            Clear all
          </Link>
        )}
      </Box>

      {!hasSelection && (
        <Typography sx={styles.emptyHint}>
          Select up to {maxAreas} areas on the map to see how your gift moves the
          needle.
        </Typography>
      )}

      <Box sx={styles.areaList}>
        {areaCards.map((area) => (
          <Box key={area.fips} sx={styles.areaCard}>
            <Box sx={styles.areaCardHead}>
              <Box>
                <Typography component="h3" sx={styles.areaName}>
                  {area.name}
                </Typography>
                <Typography sx={styles.areaType}>
                  {area.level === 'county' ? 'County' : 'State'}
                </Typography>
              </Box>
              <IconButton
                aria-label={`Remove ${area.name}`}
                onClick={() => onRemoveArea(area.fips)}
                sx={styles.removeButton}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {area.metrics ? (
              <>
                <Box sx={styles.scoreRow}>
                  <Typography component="span" sx={styles.scoreValue}>
                    {area.metrics.opportunityScore}
                  </Typography>
                  <Box sx={styles.scoreLabelGroup}>
                    <Typography component="span" sx={styles.scoreLabel}>
                      / 100 opportunity score
                    </Typography>
                    <InfoTooltip tooltip={OVERVIEW_METRIC_TOOLTIPS.opportunityScore} />
                  </Box>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={area.metrics.opportunityScore}
                  sx={styles.scoreBar}
                />

                <Box sx={styles.areaGrid}>
                  <AreaStat
                    label="Eligible under 18"
                    value={formatCompact(area.metrics.eligibleUnder18)}
                  />
                  <AreaStat
                    label="Median income"
                    value={formatCompact(area.metrics.medianIncome, true)}
                  />
                  <AreaStat
                    label="Receiving SNAP"
                    value={formatSnapRate(area.metrics.receivingSNAP)}
                    note={area.metrics.receivingSNAP > 100 ? SNAP_SUPPRESSED_NOTE : undefined}
                  />
                  <AreaStat
                    label="529 participation"
                    value={`${area.metrics.participation529}%`}
                  />
                </Box>
              </>
            ) : (
              <Typography sx={styles.emptyHint}>Loading details…</Typography>
            )}
          </Box>
        ))}
      </Box>
    </>
  );
}
